import {
  Autocomplete,
  Box,
  Checkbox,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  Paper,
  TextField
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { Account } from '../../../types';
import { HTMLAttributes, useState } from 'react';
import Text, { TextSpan } from '../../Atoms/Text';
import { truncateStr } from '../../../utils/helpers';
import { useTheme } from '@mui/material/styles';

export interface BasicAccountMultiSelectProps {
  displayAccounts: Account[];
  selectedAccounts: Account[] | null;
  handleSelectAll: () => void;
  handleSelectNone: () => void;
  handleChange: (e: React.SyntheticEvent<Element, Event>, value: Account[] | null) => void;
}

export const BasicAccountMultiSelect = ({
  displayAccounts,
  selectedAccounts,
  handleSelectAll,
  handleSelectNone,
  handleChange
}: BasicAccountMultiSelectProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const colorText = isDarkMode ? 'white' : 'black';

  const isSelected = (id: string) => {
    const selectedIds = selectedAccounts?.map((acc) => acc.id);
    return selectedIds?.includes(id);
  };

  // A single-account user has nothing to switch to, and with the input unfocusable there would be
  // no blur or Escape to close a list opened from here.
  const disabled = displayAccounts.length === 1;

  // The selected-account summary is rendered inside the input, so clicking it would otherwise
  // never reach the field. The input and the "N more" tooltip trigger keep their own handling.
  const handleBarClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;

    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.closest('button')) return;
    setListOpen(true);
  };

  const paperComponent = (paperProps: HTMLAttributes<HTMLElement>) => {
    const { children, ...restPaperProps } = paperProps;
    return (
      <Paper {...restPaperProps}>
        <Box
          onMouseDown={(e) => e.preventDefault()} // prevent blur
          pl={1.5}
          py={0.5}>
          <div className="flex space-x-4">
            <TextSpan
              size="sm14"
              color={colorText}>
              Select:{' '}
            </TextSpan>
            <button
              onClick={(e) => {
                e.preventDefault(); // prevent blur
                handleSelectAll();
              }}
              className="text-indigo">
              All
            </button>
            <button
              onClick={(e) => {
                e.preventDefault(); // prevent blur
                handleSelectNone();
              }}
              className="text-indigo">
              None
            </button>
          </div>
        </Box>
        <Divider />
        {children}
      </Paper>
    );
  };

  const getDisplay = () => {
    // Single account return here
    if (selectedAccounts?.length === 1) return selectedAccounts?.[0]?.name;

    // Multiple accounts selected.
    const more = selectedAccounts?.slice(2);
    return selectedAccounts?.length ? (
      <div className="flex items-center">
        <Text
          className="text-[.90rem] truncate"
          color={colorText}>
          {truncateStr(`${selectedAccounts?.[0]?.name}, ${selectedAccounts?.[1]?.name}`, 28)}
        </Text>
        {selectedAccounts?.length > 2 && (
          <>
            <Text color={colorText}>, and</Text>
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <Tooltip
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'common.white',
                      '& .MuiTooltip-arrow': {
                        color: 'lightgrey'
                      },
                      color: 'common.black',
                      border: '1px solid lightgrey'
                    }
                  }
                }}
                PopperProps={{
                  disablePortal: true
                }}
                onClose={() => setOpen(false)}
                open={open}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                title={
                  <pre className="font-sans text-[0.90rem] font-normal">{more?.map((a) => a.name).join('\n')}</pre>
                }>
                <button
                  className="ml-1 text-indigo"
                  onClick={() => setOpen(true)}>{`${more?.length} more`}</button>
              </Tooltip>
            </ClickAwayListener>
          </>
        )}
      </div>
    ) : null;
  };

  return (
    <div className="flex items-center w-[29rem]">
      <Autocomplete
        id="user-account-selection-input"
        data-testid="user-account-selection-input"
        value={selectedAccounts || undefined}
        disableCloseOnSelect
        multiple
        disabled={disabled}
        className="w-full"
        isOptionEqualToValue={(option, value) => option.name === value.name}
        renderTags={() => (selectedAccounts?.length ? getDisplay() : null)}
        inputValue={searchQuery}
        onInputChange={(e, val: string) => setSearchQuery(val)}
        open={listOpen}
        onOpen={() => setListOpen(true)}
        onClose={() => setListOpen(false)}
        ListboxProps={{ style: { maxHeight: 330 } }}
        onChange={handleChange}
        onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
        options={displayAccounts}
        getOptionLabel={(option: Account) => option?.name || ''}
        size="small"
        sx={{
          '.MuiInputBase-input': {
            height: '0.75rem',
            minWidth: '25px !important'
          },
          'MuiFormControl-root': {
            height: 'unset'
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            InputProps={{ ...params.InputProps, style: { fontSize: '.90rem' } }}
            className="h-[1.75rem] text-nowrap"
            data-cy="account-input"
            onClick={handleBarClick}
          />
        )}
        renderOption={(props, option) => {
          return (
            <li
              {...props}
              className={`${props['className' as keyof typeof props] || ''} text-sm14 py-1 pl-4 cursor-pointer`}
              key={`${option.id}-${option.name}`}>
              <FormControlLabel
                control={<Checkbox checked={isSelected(option.id)} />}
                label={option.name}
              />
            </li>
          );
        }}
        PaperComponent={(paperProps) => paperComponent(paperProps)}
      />
    </div>
  );
};
